/**
 * Metronic Components Test
 *
 * Test component to verify Metronic Card and Button integration works correctly
 */

import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../ui/Card';
import { Button } from '../ui/Button';
import { Target, Users, Mail, Calendar, Rocket, CheckCircle2, ArrowRight } from 'lucide-react';

export default function CardTest() {
  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Metronic Card Component Test</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Test Card 1: Basic Card */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Card</CardTitle>
              <CardDescription>This is a basic Metronic card</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                If you can see this styled card, the integration is working!
              </p>
            </CardContent>
          </Card>

          {/* Test Card 2: Metric Card Style */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-600" />
                <CardTitle>Sourced</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 mb-1">52</div>
              <div className="text-xs text-gray-500">Candidates found</div>
            </CardContent>
          </Card>

          {/* Test Card 3: With Footer */}
          <Card>
            <CardHeader>
              <CardTitle>Card with Footer</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">This card has a footer section</p>
            </CardContent>
            <CardFooter>
              <button className="text-sm text-blue-600 hover:text-blue-700">View Details</button>
            </CardFooter>
          </Card>

          {/* Test Card 4: Accent Variant */}
          <Card variant="accent">
            <CardHeader>
              <CardTitle>Accent Variant</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">This card uses the accent variant</p>
            </CardContent>
          </Card>
        </div>

        {/* Test Card 5: Campaign Metric Style */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Campaign Progress</CardTitle>
                <CardDescription>Real-time campaign metrics</CardDescription>
              </div>
              <Target className="w-6 h-6 text-orange-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold text-gray-900">52</div>
                <div className="text-xs text-gray-500">Sourced</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">18</div>
                <div className="text-xs text-gray-500">Evaluated</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">12</div>
                <div className="text-xs text-gray-500">Contacted</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">10</div>
                <div className="text-xs text-gray-500">Scheduled</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Button Examples */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Button Component Examples</CardTitle>
            <CardDescription>Various Metronic Button variants and sizes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Variants */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Button Variants</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="dashed">Dashed</Button>
                  <Button variant="destructive">Destructive</Button>
                </div>
              </div>

              {/* Sizes */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Button Sizes</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="primary" size="sm">Small</Button>
                  <Button variant="primary" size="md">Medium</Button>
                  <Button variant="primary" size="lg">Large</Button>
                </div>
              </div>

              {/* With Icons */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Buttons with Icons</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">
                    <Rocket className="w-4 h-4 mr-2" />
                    Start Campaign
                  </Button>
                  <Button variant="outline">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                  <Button variant="ghost">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Complete
                  </Button>
                  <Button variant="primary" size="icon">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Disabled States */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Disabled States</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" disabled>Disabled Primary</Button>
                  <Button variant="outline" disabled>Disabled Outline</Button>
                  <Button variant="ghost" disabled>Disabled Ghost</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Success Message */}
        <Card className="bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-green-800">✅ Integration Successful!</CardTitle>
            <CardDescription className="text-green-700">
              Both Metronic Card and Button components are working correctly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-700 mb-4">
              You can now use these components throughout your application. The components are production-ready and follow Metronic's design system.
            </p>
            <div className="flex gap-3">
              <Button variant="primary" onClick={() => window.location.href = '/campaigns/autonomous/new'}>
                <Rocket className="w-4 h-4 mr-2" />
                Try in Campaign Flow
              </Button>
              <Button variant="outline" onClick={() => window.history.back()}>
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

